<template>
  <div class="top-panel">
    <div class="coc-btn-group" v-if="canOps">
      <template v-if="currentCoc.conferenceState === conferenceState.IN_PROGRESS">
        <Button class="large-show" shape="round" type="default" @click="inviteCoc">{{$t('home.InviteCollaborators')}}</Button>
        <Tooltip placement="bottom" :title="$t('home.InviteCollaborators')">
          <Button class="mini-show circle-btn" type="default" shape="circle" @click="inviteCoc">
            <SvgIcon icon-class="add-friend-normal" style="font-size: 26px" />
          </Button>
        </Tooltip>
        <Button shape="round" type="default" @click="handleCopyInvitationLink">{{$t('home.CopyInvitationLink')}}</Button>
        <Button shape="round" type="primary" @click="joinCoc">{{$t('home.JoinCollaboration')}}</Button>
      </template>
      <template v-else>
        <template v-if="currentCoc.hostIsMe"> <!-- 主持人 -->
          <Button class="large-show" shape="round" type="default" @click="handleCancelCoc">{{$t('home.CancelCollaboration')}}</Button>
          <Button class="large-show" shape="round" type="default" @click="editCoc">{{$t('home.Edit')}}</Button>
          <Tooltip placement="bottom" :title="$t('home.CancelCollaboration')">
            <Button class="mini-show circle-btn" type="default" shape="circle" @click="handleCancelCoc">
              <SvgIcon icon-class="close" />
            </Button>
          </Tooltip>
          <Tooltip placement="bottom" :title="$t('home.Edit')">
            <Button class="mini-show circle-btn" type="default" shape="circle" @click="editCoc">
              <SvgIcon icon-class="edit-active" style="font-size: 17px" />
            </Button>
          </Tooltip>
          <Button shape="round" type="default" @click="handleCopyInvitationLink">{{$t('home.CopyInvitationLink')}}</Button>
          <Button shape="round" type="primary" @click="handleStartCoc">{{$t('home.StartCollaboration')}}</Button>
        </template>
        <template v-else>
          <template v-if="currentCoc.confirmStatus === confirmStatus.WAIT_CONFIRM">
            <Button shape="round" type="default" @click="confirmJoinCoc(false)">{{$t('common.Deny')}}</Button>
            <Button shape="round" type="primary" @click="confirmJoinCoc(true)">{{$t('home.Accept')}}</Button>
          </template>
          <template v-else>
            <Button shape="round" type="default" @click="handleCopyInvitationLink">{{$t('home.CopyInvitationLink')}}</Button>
            <Button shape="round" type="default" @click="inviteCoc">{{$t('home.InviteCollaborators')}}</Button>
          </template>
        </template>
      </template>
    </div>
    <div class="coope-title">{{ currentCoc.theme }}<span :class="cocStateClasses">{{ cocStatus }}</span></div>
    <div class="info-item">
      <div class="info-field" :style="{letterSpacing: $i18n.locale===LANGUAGE.CHINESE ? '8px' : 'normal', marginRight: '16px'}">{{$t('home.Collaborator')}}</div>
      <div class="info-content">
        <div v-for="groupMember in currentCoc.memberGroupByCorp" :key="groupMember.corpId">
          <div class="corp-tag">{{groupMember.corpName}}</div>
          <div class="corp-users">{{calcMembers(groupMember.members)}}</div>
        </div>
      </div>
    </div>
    <div class="info-item">
      <div class="info-sub-item">
        <span class="info-sub-field">{{$t('home.CollaborationTime')}}</span>
        <span class="info-sub-content alphanum">
          {{timeRange.beginTime}}
          <span class="time-through-arrow">{{timeRange.formatDuration}}</span>
          {{timeRange.endTime}}
        </span>
      </div>
    </div>
    <div class="info-item">
      <span class="info-field">{{$t('home.CollaborationType')}}</span>
      <span class="info-content">
        <div>
          <span
            v-for="tag in currentCoc.tags"
            :key="tag.id"
            class="coope-tag"
            >{{ tag.name }}</span
          >
        </div>
      </span>
    </div>
    <div v-if="canOps">
      <div class="coc-calendar">
        <div class="coc-calendar-icon" @click="handleImportCalendar"></div>
        <div class="coc-calendar-text">{{$t('home.AddCalendar')}}</div>
      </div>
    </div>
    <CollaborationAppointment ref="colAppointment" v-model="colApptVisible" />
    <InviteMoreModal
      v-model="inviteMoreModalVisible"
      type="cooperation"
      :passSelectedMembers="passSelectedMembers"
      @inviteConfirm="inviteConfirm"
      :isHost="currentCoc.hostIsMe"
    />
    <CopyInvitationLinkMixin />
  </div>
</template>
<script>
import { Button, Modal, Tooltip } from 'ant-design-vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { CONFERENCE_CONFIRM_STATUS, CONFERENCE_STATE, CONFERENCE_STATE_OPTIONS, LANGUAGE } from '@/utils/constant'
import { calcTimeRange, exportCalendar } from '@/utils/ops'
import { addCocMember, removeCocMember } from '@/api/coc'
import CollaborationAppointment from '@/components/business/collaboration-appointment/CollaborationAppointment'
import InviteMoreModal from '@/components/business/invite-more-modal'
import CopyInvitationLinkMixin from '@/components/business/copy-invitation-link-mixin/index.vue'

export default {
  name: 'CocDetail',
  mixins: [CopyInvitationLinkMixin],
  components: {
    Button,
    CollaborationAppointment,
    InviteMoreModal,
    Tooltip,
    CopyInvitationLinkMixin
  },
  data() {
    return {
      LANGUAGE: LANGUAGE,
      timeRange: {
        beginTime: '',
        endTime: '',
        formatDuration: ''
      },
      colApptVisible: false,
      inviteMoreModalVisible: false
    }
  },
  computed: {
    ...mapGetters('coc', ['currentCoc']),
    ...mapState(['userInfo']),
    cocStatus() {
      const { confirmStatus, conferenceState } = this.currentCoc
      if (confirmStatus === CONFERENCE_CONFIRM_STATUS.REJETCED) {
        return this.$t('home.Refused')
      }
      return CONFERENCE_STATE_OPTIONS[conferenceState] || this.$t('home.UnknownState')
    },
    cocStateClasses() {
      const { confirmStatus, conferenceState } = this.currentCoc
      if (confirmStatus === CONFERENCE_CONFIRM_STATUS.REJETCED) {
        return `coc-status coc-status-${CONFERENCE_STATE.CANCELED}`
      }
      return `coc-status coc-status-${conferenceState}`
    },
    canOps() {
      const { conferenceState } = this.currentCoc
      return conferenceState <= CONFERENCE_STATE.IN_PROGRESS
    },
    conferenceState() {
      return CONFERENCE_STATE
    },
    confirmStatus() {
      return CONFERENCE_CONFIRM_STATUS
    },
    passSelectedMembers() {
      return Object.keys(this.currentCoc.memberByUid)
    }
  },
  methods: {
    ...mapActions('coc', ['confirmCocInvite', 'joinConference', 'cancelCoc', 'startCoc']),
    calcMembers(members) {
      const hostId = this.currentCoc.hostId
      return members.map(({ nickName, userId }) => {
        return hostId === userId ? `${nickName}（${this.$t('home.Host')}）` : nickName
      }).join('、')
    },
    // copy() {
    //   const { beginTime, endTime, theme, tags, conferenceId } = this.currentCoc
    //   copyInvitationLink({
    //     theme,
    //     tags,
    //     beginTime,
    //     endTime,
    //     conferenceId,
    //   })
    // },
    handleImportCalendar() {
      const { beginTime, endTime, theme, tags } = this.currentCoc
      const cocTags = tags.map(({ name }) => name).join('、')
      exportCalendar({ start: new Date(beginTime * 1000), end: new Date(endTime * 1000), subject: theme, description: `${this.$t('home.CollaborationType')}: ${cocTags}` })
    },
    async confirmJoinCoc(accept) {
      try {
        await this.confirmCocInvite({
          conferenceId: this.currentCoc.conferenceId,
          confirmStatus: accept ? CONFERENCE_CONFIRM_STATUS.CONFIRMED : CONFERENCE_CONFIRM_STATUS.REJETCED
        })
      } catch (err) {
        const message = err.message
        if (message) {
          this.$message.error(message)
        }
      }
    },
    async joinCoc() {
      const { conferenceId } = this.currentCoc
      this.joinConference(conferenceId).catch((err) => {
        if (err && err.code === 22009) {
          Modal.info({
            content: this.$t('home.NotJoinIsFull'),
            width: '460px',
            centered: true,
            class: 'confirm-exit',
            closable: true,
            okText: this.$t('calling.GotIt')
          })
        } else {
          const message = err.message
          if (message) {
            this.$message.error(message)
          }
        }
      })
    },
    async handleCancelCoc() {
      try {
        await this.cancelCoc(this.currentCoc.conferenceId)
      } catch (err) {
        const message = err.message
        if (message) {
          this.$message.error(message)
        }
      }
    },
    async handleStartCoc() {
      try {
        await this.startCoc(this.currentCoc.conferenceId)
      } catch (err) {
        const message = err.message
        if (message) {
          this.$message.error(message)
        }
      }
    },
    async editCoc() {
      this.colApptVisible = true
      this.$refs.colAppointment.handleModify(this.currentCoc.conferenceId)
    },
    inviteCoc() {
      this.inviteMoreModalVisible = true
    },
    getAddAndRemoveArray(originData, newData) {
      const addMembers = []
      const removeUserIDs = []
      originData.forEach(el => {
        if (!newData.some(ele => ele.userId === el)) {
          removeUserIDs.push(el)
        }
      })
      newData.forEach(el => {
        if (!originData.some(ele => ele === el.userId)) {
          addMembers.push({
            corpId: el.corpId,
            corpName: el.corpName,
            userId: el.userId,
            nickName: el.nickName,
          })
        }
      })
      return {
        addMembers,
        removeUserIDs,
      }
    },
    inviteConfirm(members) {
      members = members.map(el => ({
        corpId: el.corpId,
        corpName: el.corpName,
        userId: el.uid,
        nickName: el.name,
      }))
      const originData = Object.keys(this.currentCoc.memberByUid)
      const { addMembers, removeUserIDs: removeIds } = this.getAddAndRemoveArray(originData, members)

      const { conferenceId } = this.currentCoc

      if (addMembers.length > 0) {
        addCocMember({ conferenceId, members: addMembers })
      }

      if (removeIds.length > 0) {
        removeCocMember({
          conferenceId, removeIds
        })
      }
    }
  },
  watch: {
    currentCoc: {
      async handler(val) {
        this.timeRange = calcTimeRange(val)
        await this.handleSetInvitationLinkOfData(this.currentCoc) // created
      },
      immediate: true
    }
  }
}
</script>
<style lang="less" scoped>
@import url(~@/styles/font.less);
@import url(~@/styles/button.less);
@import url('./detail.less');
.coc-btn-group {
  position: absolute;
  right: 20px;

  ::v-deep .ant-btn {
    margin: 0 12px;
    &.ant-btn-default {
      .default-btn-mixins();
    }
    &.ant-btn-primary {
      .primary-btn-mixins();
    }
  }
}

.large-show { display: inline-block;}
.mini-show {display: none;}

.circle-btn {
  font-size: 21px;
  width: 40px;
  height: 40px;
}

@media screen and (max-width: 1440px) {
  .large-show { display: none;}
  .mini-show {display: inline-block;}
}

.coc-calendar {
  position: absolute;
  display: table;
  right: 32px;
  bottom: 24px;
  text-align: center;

  & > &-icon {
    display: inline-block;
    width: 40px;
    height: 40px;
    background: url(~@/assets/calendar.svg) left top / cover no-repeat;
    cursor: pointer;

    &:hover {
      background-image: url(~@/assets/calendar-active.svg);
    }
  }

  & > &-text {
    .regular-font(16);
    color: #999b9f;
  }
}

.top-panel {
  position: relative;
  padding: 24px 32px;
  margin-bottom: 24px;
  background: #ffffff;
  box-shadow: 0px 5px 30px 0px rgba(89, 132, 219, 0.08);
  border-radius: 16px;
  .coope-tag {
    margin-right: 8px;
    margin-bottom: 8px;
    &:last-child {
      margin-right: 0;
    }
  }

  .coope-title {
    .semibold-font(18);
    display: flex;
    align-items: center;
    line-height: 20px;
    height: 20px;
    margin-bottom: 20px;
    color: #333333;
  }

  .info-item {
    display: flex;
    font-size: 0;
    flex-direction: row;
    align-items: flex-start;
    margin-bottom: 16px;

    & > .info-field {
      flex-shrink: 0;
    }
    & > .info-content {
      flex-grow: 1;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .info-sub-item {
    display: inline-block;
    margin-right: 64px;
  }

  .info-sub-content {
    font-size: 16px;
  }

  .alphanum {
    line-height: 1em;
  }

  .info-field,
  .info-sub-field {
    .medium-font(16);
    line-height: 16px;
    color: #333333;
    margin-right: 24px;
    vertical-align: top;
  }

  .info-content {
    margin-bottom: -8px;
    vertical-align: top;
  }

  .corp-users {
    .regular-font(14);
    color: #000;
    line-height: 20px;
    padding: 6px 8px;
  }

  .corp-tag {
    .regular-font(14);
    display: inline-block;
    color: #999B9F;
    padding-right: 8px;
    padding-left: 24px;
    border-radius: 4px;
    background: url(~@/assets/company.svg) center left / 20px 20px no-repeat, #F7F8FA;;
  }

  .time-through-arrow {
    position: relative;
    display: inline-block;

    .regular-font(12);
    height: 20px;
    color: #236CFF;
    line-height: 17px;
    background: #FAFBFD;
    padding: 2px 8px;
    margin-left: 34px;
    margin-right: 35px;

    &::before {
      position: absolute;
      top: 2px;
      left: -18px;
      content: ' ';
      display: inline-block;
      width: 18px;
      height: 100%;
      background: url(~@/assets/time-through-line.png) left top ~'7px / 100%' 2px no-repeat;
    }

    &::after {
      position: absolute;
      top: 2px;
      right: -19px;
      content: ' ';
      display: inline-block;
      width: 19px;
      height: 100%;
      background: url(~@/assets/time-arrow.png) left top ~'4px / 100%' 6px no-repeat;
    }
  }
}
</style>
